// features/customers/CustomerDetailPage.tsx
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { SettingsModal } from '@/features/settings/components/SettingsModal';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Wallet, Star, CreditCard, Phone, Mail, MapPin,
    TrendingUp, Award, RotateCcw, DollarSign,
    Gift, AlertCircle, User, Calendar, Pencil, Save
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

const TIER_STYLES: Record<string, { bg: string; text: string; border: string }> = {
    bronze: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    silver: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
    gold: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
    platinum: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
};

export function CustomerDetailPage() {
    const { customerId } = useParams<{ customerId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [activeTab, setActiveTab] = useState<'overview' | 'purchases' | 'loyalty' | 'credit' | 'debt'>('overview');
    const [showLoyaltyModal, setShowLoyaltyModal] = useState(false);
    const [showTopUpModal, setShowTopUpModal] = useState(false);
    const [showDebtModal, setShowDebtModal] = useState(false);
    const [showSettleModal, setShowSettleModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({ firstName: '', lastName: '', phone: '', email: '', dateOfBirth: '', address: '' });
    const [loyaltyForm, setLoyaltyForm] = useState({ points: '', notes: '' });
    const [topUpForm, setTopUpForm] = useState({ amount: '', paymentMethod: 'cash' });
    const [debtForm, setDebtForm] = useState({ amount: '', notes: '' });
    const [settleForm, setSettleForm] = useState({ amount: '', paymentMethod: 'cash' });

    const { data: customer, isLoading, isError, refetch } = useQuery({
        queryKey: ['customer', customerId],
        queryFn: async () => { const { data } = await apiClient.get(`/tenants/${user!.tenantId}/customers/${customerId}`); return data.data; },
        enabled: !!user?.tenantId && !!customerId,
    });

    const { data: purchases } = useQuery({
        queryKey: ['customer-purchases', customerId],
        queryFn: async () => { const { data } = await apiClient.get(`/tenants/${user!.tenantId}/customers/${customerId}/purchases`); return data?.data || []; },
        enabled: !!user?.tenantId && !!customerId && activeTab === 'purchases',
    });

    const { data: loyaltyHistory } = useQuery({
        queryKey: ['customer-loyalty', customerId],
        queryFn: async () => { const { data } = await apiClient.get(`/tenants/${user!.tenantId}/customers/${customerId}/loyalty`); return data?.data || []; },
        enabled: !!user?.tenantId && !!customerId && activeTab === 'loyalty',
    });

    const { data: creditHistory } = useQuery({
        queryKey: ['customer-credit', customerId],
        queryFn: async () => { const { data } = await apiClient.get(`/tenants/${user!.tenantId}/customers/${customerId}/store-credit`); return data?.data || []; },
        enabled: !!user?.tenantId && !!customerId && activeTab === 'credit',
    });

    const { data: debtHistory } = useQuery({
        queryKey: ['customer-debt', customerId],
        queryFn: async () => { const { data } = await apiClient.get(`/tenants/${user!.tenantId}/customers/${customerId}/debt`); return data?.data || []; },
        enabled: !!user?.tenantId && !!customerId && activeTab === 'debt',
    });

    const adjustLoyaltyMutation = useMutation({
        mutationFn: () => apiClient.post(`/tenants/${user!.tenantId}/customers/${customerId}/loyalty/adjust`, loyaltyForm),
        onSuccess: () => { addToast({ type: 'success', title: 'Points adjusted' }); setShowLoyaltyModal(false); refetch(); },
    });

    const topUpMutation = useMutation({
        mutationFn: () => apiClient.post(`/tenants/${user!.tenantId}/customers/${customerId}/top-up`, topUpForm),
        onSuccess: () => { addToast({ type: 'success', title: 'Credit topped up' }); setShowTopUpModal(false); refetch(); },
    });

    const recordDebtMutation = useMutation({
        mutationFn: () => apiClient.post(`/tenants/${user!.tenantId}/customers/${customerId}/debt`, {
            ...debtForm, saleId: '00000000-0000-0000-0000-000000000000'
        }),
        onSuccess: () => { addToast({ type: 'success', title: 'Debt recorded' }); setShowDebtModal(false); refetch(); },
    });

    const settleDebtMutation = useMutation({
        mutationFn: () => apiClient.post(`/tenants/${user!.tenantId}/customers/${customerId}/debt/settle`, settleForm),
        onSuccess: () => { addToast({ type: 'success', title: 'Debt settled' }); setShowSettleModal(false); refetch(); },
    });

    const updateMutation = useMutation({
        mutationFn: (payload: any) => apiClient.patch(`/tenants/${user!.tenantId}/customers/${customerId}`, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            setShowEditModal(false);
            addToast({ type: 'success', title: 'Customer updated' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Update failed', message: err?.response?.data?.error?.message }),
    });

    const formatNaira = (val: string) => `₦${parseFloat(val || '0').toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatDate = (dateStr: string) => { if (!dateStr) return '—'; return new Date(dateStr).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }); };

    if (isLoading) return <div className="flex justify-center py-20"><Spinner size="xl" /></div>;
    if (isError || !customer) return <ErrorState title="Customer not found" onRetry={() => refetch()} />;

    const tier = TIER_STYLES[customer.loyaltyTier] || TIER_STYLES.bronze;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
            {/* Premium Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/customers')} className="p-2 rounded-xl hover:bg-slate-100 transition-colors" type="button"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-md">{customer.firstName?.[0]}{customer.lastName?.[0]}</div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-2xl font-bold text-slate-900">{customer.firstName} {customer.lastName}</h1>
                                    <Badge variant="success" size="sm">{customer.isActive ? 'Active' : 'Inactive'}</Badge>
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border', tier.bg, tier.text, tier.border)}><Award size={12} />{customer.loyaltyTier}</span>
                                    <span className="text-sm text-slate-400">{customer.phone}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => {
                                setEditForm({
                                    firstName: customer.firstName || '',
                                    lastName: customer.lastName || '',
                                    phone: customer.phone || '',
                                    email: customer.email || '',
                                    dateOfBirth: customer.dateOfBirth ? String(customer.dateOfBirth).slice(0, 10) : '',
                                    address: customer.address || '',
                                });
                                setShowEditModal(true);
                            }} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium shadow-sm" type="button"><Pencil size={16} /> Edit Profile</button>
                            <button onClick={() => setShowTopUpModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium shadow-sm" type="button"><Wallet size={16} /> Top Up</button>
                            <button onClick={() => setShowLoyaltyModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium shadow-sm" type="button"><Star size={16} /> Adjust Points</button>
                            <button onClick={() => setShowDebtModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium shadow-sm" type="button"><CreditCard size={16} /> Record Debt</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard label="Total Spent" value={formatNaira(customer.totalSpent)} icon={DollarSign} gradient="from-emerald-500 to-teal-600" />
                    <StatCard label="Loyalty Points" value={customer.loyaltyPoints} icon={Star} gradient="from-amber-500 to-orange-600" />
                    <StatCard label="Store Credit" value={formatNaira(customer.storeCredit)} icon={Wallet} gradient="from-blue-500 to-indigo-600" />
                    <StatCard label="Outstanding Debt" value={formatNaira(customer.debtBalance)} icon={CreditCard} gradient="from-rose-500 to-pink-600" warning={parseFloat(customer.debtBalance) > 0} />
                </div>

                {/* Contact Info */}
                <Card>
                    <CardContent className="p-5 grid grid-cols-2 md:grid-cols-5 gap-4">
                        <InfoItem icon={Phone} label="Phone" value={customer.phone} />
                        <InfoItem icon={Mail} label="Email" value={customer.email || '—'} />
                        <InfoItem icon={Calendar} label="Date of Birth" value={customer.dateOfBirth ? formatDate(customer.dateOfBirth) : '—'} />
                        <InfoItem icon={MapPin} label="Address" value={customer.address || '—'} />
                        <InfoItem icon={Calendar} label="Customer Since" value={formatDate(customer.createdAt)} />
                    </CardContent>
                </Card>

                {/* Tabs */}
                <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
                    {[
                        { id: 'overview' as const, label: 'Overview', icon: User },
                        { id: 'purchases' as const, label: 'Purchases', icon: TrendingUp, count: purchases?.length },
                        { id: 'loyalty' as const, label: 'Loyalty', icon: Gift, count: loyaltyHistory?.length },
                        { id: 'credit' as const, label: 'Store Credit', icon: Wallet, count: creditHistory?.length },
                        { id: 'debt' as const, label: 'Debt', icon: CreditCard, count: debtHistory?.length },
                    ].map((t) => (
                        <button key={t.id} onClick={() => setActiveTab(t.id)}
                            className={clsx('flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all', activeTab === t.id ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500')} type="button">
                            <t.icon size={16} />{t.label}
                            {t.count !== undefined && t.count > 0 && <Badge variant="neutral" size="sm">{t.count}</Badge>}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                {activeTab === 'purchases' && (
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead><tr className="bg-slate-50/50">
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Order #</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Amount</th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Date</th>
                                </tr></thead>
                                <tbody className="divide-y divide-slate-50">
                                    {(purchases || []).map((p: any) => (
                                        <tr key={p.id} className="hover:bg-slate-50">
                                            <td className="px-6 py-4"><p className="text-sm font-mono font-medium">{p.orderNumber}</p></td>
                                            <td className="px-6 py-4 text-right text-sm font-semibold">{formatNaira(p.grandTotal)}</td>
                                            <td className="px-6 py-4 text-center"><Badge variant={p.status === 'completed' ? 'success' : 'neutral'} size="sm">{p.status}</Badge></td>
                                            <td className="px-6 py-4 text-right text-sm text-slate-500">{formatDate(p.createdAt)}</td>
                                        </tr>
                                    ))}
                                    {(!purchases || purchases.length === 0) && <tr><td colSpan={4} className="text-center py-8 text-sm text-slate-400">No purchases yet</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}

                {activeTab === 'loyalty' && <HistoryTable data={loyaltyHistory || []} columns={['Points', 'Balance', 'Type', 'Notes', 'Date']} emptyText="No loyalty transactions" />}
                {activeTab === 'credit' && <HistoryTable data={creditHistory || []} columns={['Amount', 'Balance', 'Type', 'Method', 'Date']} emptyText="No store credit transactions" />}
                {activeTab === 'debt' && <HistoryTable data={debtHistory || []} columns={['Amount', 'Balance', 'Status', 'Notes', 'Date']} emptyText="No debt transactions" />}

                {/* Debt Settlement */}
                {activeTab === 'debt' && parseFloat(customer.debtBalance) > 0 && (
                    <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-200 rounded-2xl">
                        <AlertCircle size={20} className="text-rose-500" />
                        <div className="flex-1"><p className="text-sm font-semibold text-rose-700">Outstanding Debt: {formatNaira(customer.debtBalance)}</p></div>
                        <button onClick={() => { setSettleForm({ amount: customer.debtBalance, paymentMethod: 'cash' }); setShowSettleModal(true); }}
                            className="flex items-center gap-2 px-4 py-2.5 bg-rose-500 text-white rounded-xl hover:bg-rose-600 font-semibold text-sm" type="button">
                            <RotateCcw size={16} /> Settle Debt
                        </button>
                    </div>
                )}
            </div>

            {/* Modals */}
            <SettingsModal open={showLoyaltyModal} onClose={() => setShowLoyaltyModal(false)} title="Adjust Loyalty Points" icon={Star}
                footer={<><Button variant="secondary" onClick={() => setShowLoyaltyModal(false)}>Cancel</Button><Button onClick={() => adjustLoyaltyMutation.mutate()} isLoading={adjustLoyaltyMutation.isPending} disabled={!loyaltyForm.points || !loyaltyForm.notes}>Confirm</Button></>}>
                <div className="space-y-4">
                    <Input label="Points (use negative to deduct)" type="number" value={loyaltyForm.points} onChange={(e) => setLoyaltyForm({ ...loyaltyForm, points: e.target.value })} />
                    <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Reason *</label><textarea value={loyaltyForm.notes} onChange={(e) => setLoyaltyForm({ ...loyaltyForm, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border-2 border-slate-200 rounded-xl text-sm resize-none" required /></div>
                </div>
            </SettingsModal>

            <SettingsModal open={showTopUpModal} onClose={() => setShowTopUpModal(false)} title="Top Up Store Credit" icon={Wallet}
                footer={<><Button variant="secondary" onClick={() => setShowTopUpModal(false)}>Cancel</Button><Button onClick={() => topUpMutation.mutate()} isLoading={topUpMutation.isPending} disabled={!topUpForm.amount}>Top Up</Button></>}>
                <div className="space-y-4">
                    <Input label="Amount *" type="number" step="0.01" value={topUpForm.amount} onChange={(e) => setTopUpForm({ ...topUpForm, amount: e.target.value })} />
                    <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Payment Method</label>
                        <select value={topUpForm.paymentMethod} onChange={(e) => setTopUpForm({ ...topUpForm, paymentMethod: e.target.value })} className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm">
                            <option value="cash">Cash</option><option value="bank_transfer">Bank Transfer</option><option value="pos_card">POS Card</option><option value="mobile_money">Mobile Money</option></select></div>
                </div>
            </SettingsModal>

            <SettingsModal open={showDebtModal} onClose={() => setShowDebtModal(false)} title="Record Debt" icon={CreditCard}
                footer={<><Button variant="secondary" onClick={() => setShowDebtModal(false)}>Cancel</Button><Button onClick={() => recordDebtMutation.mutate()} isLoading={recordDebtMutation.isPending} disabled={!debtForm.amount}>Record</Button></>}>
                <div className="space-y-4">
                    <Input label="Amount *" type="number" step="0.01" value={debtForm.amount} onChange={(e) => setDebtForm({ ...debtForm, amount: e.target.value })} />
                    <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Notes</label><textarea value={debtForm.notes} onChange={(e) => setDebtForm({ ...debtForm, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border-2 border-slate-200 rounded-xl text-sm resize-none" /></div>
                </div>
            </SettingsModal>

            <SettingsModal open={showSettleModal} onClose={() => setShowSettleModal(false)} title="Settle Debt" icon={RotateCcw}
                footer={<><Button variant="secondary" onClick={() => setShowSettleModal(false)}>Cancel</Button><Button onClick={() => settleDebtMutation.mutate()} isLoading={settleDebtMutation.isPending} disabled={!settleForm.amount}>Settle</Button></>}>
                <div className="space-y-4">
                    <Input label="Amount *" type="number" step="0.01" value={settleForm.amount} onChange={(e) => setSettleForm({ ...settleForm, amount: e.target.value })} />
                    <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Payment Method</label>
                        <select value={settleForm.paymentMethod} onChange={(e) => setSettleForm({ ...settleForm, paymentMethod: e.target.value })} className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm">
                            <option value="cash">Cash</option><option value="bank_transfer">Bank Transfer</option><option value="pos_card">POS Card</option><option value="mobile_money">Mobile Money</option></select></div>
                </div>
            </SettingsModal>

            <SettingsModal open={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Customer Details" icon={Pencil}
                footer={<><Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button><Button onClick={() => updateMutation.mutate(editForm)} isLoading={updateMutation.isPending} disabled={!editForm.firstName || !editForm.lastName || !editForm.phone}>Save Changes</Button></>}>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <Input label="First Name *" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} required />
                        <Input label="Last Name *" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Input label="Phone Number *" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} required />
                        <Input label="Email Address" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Date of Birth</label>
                        <input
                            type="date"
                            value={editForm.dateOfBirth}
                            onChange={(e) => setEditForm({ ...editForm, dateOfBirth: e.target.value })}
                            className="w-full px-3 py-2 border-2 border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:outline-none"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Address</label>
                        <textarea
                            value={editForm.address}
                            onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                            rows={2}
                            className="w-full px-3 py-2 border-2 border-slate-200 rounded-xl text-sm resize-none focus:border-teal-500 focus:outline-none"
                        />
                    </div>
                </div>
            </SettingsModal>
        </div>
    );
}

const StatCard: React.FC<{ label: string; value: string | number; icon: React.ElementType; gradient: string; warning?: boolean }> = ({ label, value, icon: Icon, gradient, warning }) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
        className={clsx('bg-white rounded-2xl border shadow-sm p-5 hover:shadow-lg transition-all duration-300', warning ? 'border-rose-200' : 'border-slate-200')}>
        <div className="flex items-start justify-between">
            <div className="space-y-1"><p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p><p className="text-3xl font-bold text-slate-900 mt-1">{value}</p></div>
            <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}><Icon className="w-5 h-5 text-white" /></div>
        </div>
    </motion.div>
);

const InfoItem: React.FC<{ icon: React.ElementType; label: string; value: string }> = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-slate-100"><Icon size={16} className="text-slate-500" /></div>
        <div><p className="text-xs text-slate-400">{label}</p><p className="text-sm font-medium text-slate-700">{value}</p></div>
    </div>
);

const HistoryTable: React.FC<{ data: any[]; columns: string[]; emptyText: string }> = ({ data, columns, emptyText }) => (
    <Card className="overflow-hidden">
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
                <thead><tr className="bg-slate-50/50">{columns.map(col => <th key={col} className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{col}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-50">
                    {data.length === 0 ? <tr><td colSpan={columns.length} className="text-center py-8 text-sm text-slate-400">{emptyText}</td></tr> :
                        data.map((item: any, i: number) => (
                            <tr key={item.id || i} className="hover:bg-slate-50">
                                {columns.map(col => <td key={col} className="px-6 py-4 text-sm text-slate-600">{item[col.toLowerCase()] || item[col] || '—'}</td>)}
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    </Card>
);